// Simple Firestore Service using Local Backend
// This service communicates with our local backend to avoid CORS issues

import { Car } from '../types';

export interface FirestoreResult {
  success: boolean;
  data?: any;
  error?: string;
}

class SimpleFirestoreService {
  private baseUrl: string;

  constructor() {
    // Use environment variable for backend URL, with fallback to localhost
    const envBase = (import.meta.env as any).VITE_API_BASE_URL as string | undefined;
    console.log('[Firestore Service] VITE_API_BASE_URL from env:', envBase);
    
    let apiBase: string;
    if (envBase && envBase.trim().length > 0) {
      apiBase = envBase.trim().replace(/\/$/, '');
    } else if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      apiBase = 'http://localhost:3002';
    } else {
      apiBase = '';
    }
    
    this.baseUrl = `${apiBase}/api/firestore`;
    console.log('[Firestore Service] Final baseUrl:', this.baseUrl);
  }

  /**
   * Make a request to our backend
   */
  private async makeRequest(endpoint: string, method: string = 'GET', body?: any): Promise<FirestoreResult> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      console.log(`🌐 ${method} ${url}`);
      
      const options: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
        }
      };
      
      // Only add body for non-GET/HEAD methods
      if (body && method !== 'GET' && method !== 'HEAD') {
        options.body = JSON.stringify(body);
        console.log('📤 Request body:', body);
      }
      
      const response = await fetch(url, options);
      const data = await response.json();
      
      console.log(`📥 Response ${response.status}:`, data);
      
      if (response.ok && data.success !== false) {
        return {
          success: true,
          data: data.data || data
        };
      } else {
        return {
          success: false,
          error: data.error || `Request failed with status ${response.status}`
        };
      }
    } catch (error) {
      console.error('❌ Request error:', error);
      return {
        success: false,
        error: `Request failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Test connection
   */
  async testConnection(): Promise<FirestoreResult> {
    console.log('🔍 Testing backend connection...');
    return this.makeRequest('/test');
  }

  /**
   * Get all cars
   */
  async getAllCars(): Promise<FirestoreResult> {
    console.log('📡 Getting all cars...');
    const result = await this.makeRequest('/cars');
    
    if (result.success && result.data?.documents) {
      // Convert Firestore format to our Car format
      const cars = result.data.documents.map((doc: any) => {
        const carId = doc.name.split('/').pop();
        const carData = this.convertFromFirestoreFormat(doc.fields);
        return {
          ...carData,
          id: carId
        };
      });
      
      return {
        success: true,
        data: cars
      };
    }
    
    return result;
  }

  /**
   * Get car by ID
   */
  async getCarById(carId: string): Promise<FirestoreResult> {
    console.log('🔍 Getting car by ID:', carId);
    const result = await this.makeRequest(`/cars/${carId}`);
    
    if (result.success && result.data?.fields) {
      const carData = this.convertFromFirestoreFormat(result.data.fields);
      return {
        success: true,
        data: {
          ...carData,
          id: carId
        }
      };
    }
    
    return result;
  }

  /**
   * Add a new car
   */
  async addCar(car: Omit<Car, 'id'>): Promise<FirestoreResult> {
    console.log('➕ Adding car:', car);
    
    // Generate a unique document ID
    const carId = `car_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    
    // Prepare car data for Firestore format
    const carData = {
      ...car,
      id: carId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const firestoreDoc = this.convertToFirestoreFormat(carData);
    
    return this.makeRequest(`/cars/${carId}`, 'POST', {
      fields: firestoreDoc
    });
  }

  /**
   * Update a car
   */
  async updateCar(carId: string, updates: Partial<Car>): Promise<FirestoreResult> {
    console.log('🔄 Updating car:', carId, 'with:', updates);
    
    // Try to get the existing car, but don't fail if it doesn't exist
    const existingResult = await this.getCarById(carId);
    let mergedData;
    
    if (existingResult.success) {
      // Merge with existing data
      mergedData = {
        ...existingResult.data,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    } else {
      // Car doesn't exist, use updates as base data
      console.log('⚠️ Car not found, creating new document with updates');
      mergedData = {
        id: carId,
        ...updates,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
    
    const firestoreDoc = this.convertToFirestoreFormat(mergedData);
    
    return this.makeRequest(`/cars/${carId}`, 'PUT', {
      fields: firestoreDoc
    });
  }

  /**
   * Delete a car
   */
  async deleteCar(carId: string): Promise<FirestoreResult> {
    console.log('🗑️ Deleting car:', carId);
    return this.makeRequest(`/cars/${carId}`, 'DELETE');
  }

  /**
   * Convert JavaScript object to Firestore format
   */
  private convertToFirestoreFormat(obj: any): any {
    const result: any = {};
    
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) {
        result[key] = { nullValue: null };
      } else if (typeof value === 'string') {
        result[key] = { stringValue: value };
      } else if (typeof value === 'number') {
        result[key] = Number.isInteger(value) ? { integerValue: value } : { doubleValue: value };
      } else if (typeof value === 'boolean') {
        result[key] = { booleanValue: value };
      } else if (Array.isArray(value)) {
        result[key] = {
          arrayValue: {
            values: value.map(item => 
              typeof item === 'string' ? { stringValue: item } : { stringValue: String(item) }
            )
          }
        };
      } else if (value instanceof Date) {
        result[key] = { timestampValue: value.toISOString() };
      } else {
        result[key] = { stringValue: String(value) };
      }
    }
    
    return result;
  }

  /**
   * Convert Firestore format to JavaScript object
   */
  private convertFromFirestoreFormat(fields: any): any {
    const result: any = {};
    
    for (const [key, value] of Object.entries(fields)) {
      const fieldValue = value as any;
      
      if (fieldValue.stringValue !== undefined) {
        result[key] = fieldValue.stringValue;
      } else if (fieldValue.integerValue !== undefined) {
        result[key] = parseInt(fieldValue.integerValue);
      } else if (fieldValue.doubleValue !== undefined) {
        result[key] = parseFloat(fieldValue.doubleValue);
      } else if (fieldValue.booleanValue !== undefined) {
        result[key] = fieldValue.booleanValue;
      } else if (fieldValue.arrayValue !== undefined) {
        result[key] = fieldValue.arrayValue.values?.map((item: any) => 
          item.stringValue || item.integerValue || item.doubleValue || item.booleanValue
        ) || [];
      } else if (fieldValue.timestampValue !== undefined) {
        result[key] = new Date(fieldValue.timestampValue);
      } else if (fieldValue.nullValue !== undefined) {
        result[key] = null;
      } else {
        result[key] = fieldValue;
      }
    }
    
    // Add default saleStatus if not present (for backward compatibility)
    if (!result.saleStatus) {
      result.saleStatus = 'available';
    }
    
    return result;
  }

  /**
   * Get current access token (not needed for backend approach)
   */
  async getCurrentAccessToken(): Promise<string> {
    return 'not-needed-with-backend';
  }

  /**
   * Handle token expiry (not needed for backend approach)
   */
  // Token expiry handling is not required for backend approach

  /**
   * Get configuration info
   */
  getConfigInfo() {
    return {
      service: 'Backend Proxy',
      backendUrl: this.baseUrl,
      isConfigured: true
    };
  }
}

// Export singleton instance
export const simpleFirestoreService = new SimpleFirestoreService();
export default simpleFirestoreService;